import { useCallback } from "react";
import { useNavigate } from "react-router";
import { BrowseNavigationTo, BrowsePage } from "@/types/browse.type";
import { CommonURLKey } from "@/constants/URLKeys.constant";
import {
  ROUTE_BROWSE_JOBS_INTERACTIVE,
  ROUTE_BROWSE_JOBS_LIST,
  ROUTE_BROWSE_LOGS_INTERACTIVE,
  ROUTE_BROWSE_LOGS_LIST,
  ROUTE_BROWSE_RELAYS_LIST,
  ROUTE_BROWSE_RELAYS_INTERACTIVE,
  ROUTE_BROWSE_SENSORS_LIST,
  ROUTE_BROWSE_SENSORS_INTERACTIVE,
  ROUTE_BROWSE_SESSIONS_INTERACTIVE,
  ROUTE_BROWSE_SESSIONS_LIST,
  ROUTE_BROWSE_WAYPOINTS_INTERACTIVE,
  ROUTE_BROWSE_WAYPOINTS_LIST
} from "@/constants/Route.constant";
import { useBrowseMatch } from "./useBrowseMatch";

type BrowseNavigationProps = {
  to: BrowseNavigationTo;
};

export const ROUTE_MAP: Record<BrowseNavigationTo, string> = {
  "waypoints-interactive": ROUTE_BROWSE_WAYPOINTS_INTERACTIVE,
  "waypoints-list": ROUTE_BROWSE_WAYPOINTS_LIST,
  "sessions-interactive": ROUTE_BROWSE_SESSIONS_INTERACTIVE,
  "sessions-list": ROUTE_BROWSE_SESSIONS_LIST,
  "sensors-interactive": ROUTE_BROWSE_SENSORS_INTERACTIVE,
  "sensors-list": ROUTE_BROWSE_SENSORS_LIST,
  "relays-interactive": ROUTE_BROWSE_RELAYS_INTERACTIVE,
  "relays-list": ROUTE_BROWSE_RELAYS_LIST,
  "jobs-interactive": ROUTE_BROWSE_JOBS_INTERACTIVE,
  "jobs-list": ROUTE_BROWSE_JOBS_LIST,
  "logs-interactive": ROUTE_BROWSE_LOGS_INTERACTIVE,
  "logs-list": ROUTE_BROWSE_LOGS_LIST
};

// Non-persistent URL keys that should be cleared on navigation
const NON_PERSISTENT_KEYS = [
  CommonURLKey.PAGE,
  CommonURLKey.PER_PAGE,
  CommonURLKey.SORT_BY,
  CommonURLKey.SORT_ORDER,
  CommonURLKey.SELECTED_FEATURE
];

// Common filters should be cleared when switching tabs, not when switching view modes.
// TODO: COMMON_FILTERS - revert these changes after logic change
// const NON_PERSISTENT_KEYS = [
//   CommonURLKey.PAGE,
//   CommonURLKey.PER_PAGE,
//   CommonURLKey.SORT_BY,
//   CommonURLKey.SORT_ORDER,
//   CommonURLKey.SELECTED_FEATURE,
//   CommonURLKey.PROJECT_IDS,
//   CommonURLKey.COMPANY_IDS,
//   CommonURLKey.PARTNER_IDS,
//   CommonURLKey.DMA_IDS
// ];
// ---
const TAB_ONLY_NON_PERSISTENT_KEYS = [
  CommonURLKey.PROJECT_IDS,
  CommonURLKey.COMPANY_IDS,
  CommonURLKey.PARTNER_IDS,
  CommonURLKey.DMA_IDS
] as const;

export const useBrowseNavigation = () => {
  const navigate = useNavigate();

  const {
    isJobs,
    isLogs,
    isRelays,
    isSessions,
    isSensors,
    isWaypoints,
    isInteractive
  } = useBrowseMatch();

  const isCurrentBrowsePage = useCallback(
    (page: BrowsePage) => {
      if (isWaypoints && page === "waypoints") {
        return true;
      } else if (isSensors && page === "sensors") {
        return true;
      } else if (isSessions && page === "sessions") {
        return true;
      } else if (isRelays && page === "relays") {
        return true;
      } else if (isJobs && page === "jobs") {
        return true;
      } else if (isLogs && page === "logs") {
        return true;
      }
      return false;
    },
    [isWaypoints, isSensors, isSessions, isRelays, isJobs, isLogs]
  );

  const navigateBrowse = useCallback(
    async (
      props: BrowseNavigationProps & { preserveCommonFilters?: boolean }
    ) => {
      const targetPath = ROUTE_MAP[props.to];

      // Read current URL params and clear only non-persistent ones
      const currentParams = new URLSearchParams(window.location.search);
      NON_PERSISTENT_KEYS.forEach((key) => {
        currentParams.delete(key);
      });
      if (!props.preserveCommonFilters) {
        TAB_ONLY_NON_PERSISTENT_KEYS.forEach((key) => {
          currentParams.delete(key);
        });
      }

      const cleanedSearch = currentParams.toString();

      await navigate({
        pathname: targetPath,
        search: cleanedSearch ? `?${cleanedSearch}` : ""
      });
    },
    [navigate]
  );

  const switchViewMode = useCallback(async () => {
    let targetPage: BrowsePage;

    if (isWaypoints) {
      targetPage = "waypoints";
    } else if (isSensors) {
      targetPage = "sensors";
    } else if (isSessions) {
      targetPage = "sessions";
    } else if (isRelays) {
      targetPage = "relays";
    } else if (isJobs) {
      targetPage = "jobs";
    } else if (isLogs) {
      targetPage = "logs";
    }

    await navigateBrowse({
      to: `${targetPage}-${isInteractive ? "list" : "interactive"}`,
      preserveCommonFilters: true
    });
  }, [
    isJobs,
    isLogs,
    isRelays,
    isSensors,
    isSessions,
    isWaypoints,
    isInteractive,
    navigateBrowse
  ]);

  const navigateBrowsePage = useCallback(
    async (props: { to: BrowsePage; preserveCommonFilters?: boolean }) => {
      if (isCurrentBrowsePage(props.to)) {
        return;
      }

      await navigateBrowse({
        to: `${props.to}-${isInteractive ? "interactive" : "list"}`,
        preserveCommonFilters: props.preserveCommonFilters
      });
    },
    [navigateBrowse, isInteractive, isCurrentBrowsePage]
  );

  return {
    switchViewMode,
    navigateBrowse,
    navigateBrowsePage
  };
};
